import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { FlipHorizontal, Camera, CameraOff } from "lucide-react";

interface CameraSlotProps {
  fillHeight?: boolean;
  isMirrored: boolean;
  onToggleMirror: () => void;
  cameraActive: boolean;
  error: string | null;
  videoRef: RefObject<HTMLVideoElement>;
  onStartCamera: () => void;
  onStopCamera: () => void;
}

export function CameraSlot({
  fillHeight = false,
  isMirrored,
  onToggleMirror,
  cameraActive,
  error,
  videoRef,
  onStartCamera,
  onStopCamera,
}: CameraSlotProps) {
  return (
    <div className={`flex flex-col min-w-0 ${fillHeight ? "h-full" : ""}`}>
      {/* Slot header */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-card border-b shrink-0">
        <span className="w-6 h-6 rounded bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
          B
        </span>
        <span className="text-sm font-medium truncate flex-1">Your Camera</span>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shrink-0"
          onClick={onToggleMirror}
          title={isMirrored ? "Mirrored" : "Normal"}
        >
          <FlipHorizontal className={`w-4 h-4 ${isMirrored ? "text-primary" : "text-muted-foreground"}`} />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2 shrink-0"
          onClick={cameraActive ? onStopCamera : onStartCamera}
        >
          {cameraActive ? "Stop" : "Enable Camera"}
        </Button>
      </div>

      {/* Video area */}
      <div className={`relative bg-black overflow-hidden ${fillHeight ? "flex-1" : "aspect-video w-full"}`}>
        {/* Always render the video element so the ref is available when the stream starts */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{
            display: cameraActive ? "block" : "none",
            transform: isMirrored ? "scaleX(-1)" : "none",
          }}
        />

        {!cameraActive && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Camera className="w-10 h-10 text-muted-foreground opacity-40" />
            <Button variant="outline" onClick={onStartCamera}>Enable Camera</Button>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
            <CameraOff className="w-10 h-10 text-destructive opacity-60" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button variant="outline" size="sm" onClick={onStartCamera}>Try Again</Button>
          </div>
        )}
      </div>
    </div>
  );
}
